// Identity evidence for React's RECOVERABLE client errors — the hydration
// family (#418 mismatch, #419/#422 Suspense bailout, #423 root, #425 text).
//
// WHY this file exists: in production React strips the mismatch diff, so the
// error reaching `window` is the opaque string
// `Minified React error #418; visit https://react.dev/errors/418?args[]=…`.
// Next.js 16 makes it worse — `next/dist/client/react-client-callbacks/
// on-recoverable-error.js` declares `onRecoverableError = (error) => …`, i.e.
// it DISCARDS React's second argument (`errorInfo.componentStack`) one frame
// before handing the error to `reportGlobalError` → `window.reportError`. There
// is no userland hook to replace that callback in the App Router, so the real
// component stack is unreachable from application code. What IS reachable is
// the DOM the mismatch leaves behind, which this module turns into a stable,
// PII-free identity string: the container React regenerated, the digest React
// read off the server's `<template data-dgst>`, and the bailout templates still
// in the document. Two investigations (2026-08-24, 2026-08-25) blamed the wrong
// cause for the live #418 because that identity was never captured.
//
// Everything here is pure + DOM-read-only so it can be exercised in jsdom; the
// listener and the mutation witness live in `hydration-error-reporter.ts`.

import { scrubPii } from "@/lib/pii-scrub";

/** React's recoverable-hydration error codes → a stable, queryable class name. */
const RECOVERABLE_CLASSES: Readonly<Record<string, string>> = {
  // Verified 2026-08-25 against next/dist/compiled/react-dom (React 19.2.3):
  // `throwOnHydrationMismatch` builds `Error(formatProdErrorMessage(418, …))`.
  "418": "hydration_mismatch",
  // React reads `dgst` off the server's `<template data-dgst>` sibling and puts
  // it on `error.digest` for this one — the only code that carries a digest.
  "419": "suspense_server_aborted",
  "421": "suspense_update_before_hydrate",
  "422": "suspense_hydration_error",
  "423": "root_hydration_error",
  "425": "text_content_mismatch",
};

// Production messages carry the code twice (`#418` and the react.dev URL);
// development messages carry no code at all, so they map by their exact text.
const MINIFIED_CODE_RE = /(?:react\.dev\/errors\/|Minified React error #)(\d{3})/;

const DEV_MESSAGE_CODES: ReadonlyArray<readonly [string, string]> = [
  ["Hydration failed because the server rendered", "418"],
  ["The server could not finish this Suspense boundary", "419"],
  ["received an update before it finished hydrating", "421"],
  ["client rendering from the nearest Suspense boundary", "422"],
  ["client rendering the entire root", "423"],
  ["Text content does not match server-rendered HTML", "425"],
];

/**
 * The React recoverable-error code carried by `message`, or null when the
 * message is not one of them. Returning null is load-bearing: every other error
 * stays owned by `GlobalErrorReporter`, so this path can never change the
 * shaping of an unrelated `js_error`.
 */
export function reactRecoverableCode(message: string): string | null {
  const minified = MINIFIED_CODE_RE.exec(message);
  if (minified && RECOVERABLE_CLASSES[minified[1]]) return minified[1];
  for (const [needle, code] of DEV_MESSAGE_CODES) {
    if (message.includes(needle)) return code;
  }
  return null;
}

/** The queryable class name for a code, or `unknown` for an unmapped one. */
export function recoverableClass(code: string): string {
  return RECOVERABLE_CLASSES[code] ?? "unknown";
}

const MAX_TOKEN_CHARS = 32;
const PATH_DEPTH = 3;
const MAX_PATH_CHARS = 72;

/**
 * One element's identity token, built from a WHITELIST of structural
 * attributes. Never text content, never a value attribute: this string is
 * emitted as the beacon's `component` param, which `reportJsError` does NOT
 * scrub (only message/stack/page_location are), so the scrubbing obligation of
 * rule 65 lands here and in `buildHydrationEvidence`.
 */
function identityToken(el: Element): string {
  const tag = el.tagName.toLowerCase().slice(0, MAX_TOKEN_CHARS);
  const id = el.id ? `#${el.id.slice(0, MAX_TOKEN_CHARS)}` : "";
  const marker = el.getAttribute("data-testid") ?? el.getAttribute("data-component");
  const named = marker ? `[${marker.slice(0, MAX_TOKEN_CHARS)}]` : "";
  const firstClass = (el.getAttribute("class") ?? "").trim().split(/\s+/)[0] ?? "";
  // A class token only helps when nothing better names the element.
  const cls = !id && !named && firstClass ? `.${firstClass.slice(0, MAX_TOKEN_CHARS)}` : "";
  return `${tag}${id}${named}${cls}`;
}

// `html>body>` is on every path and names nothing — spend the bounded depth on
// the ancestors that actually locate the failure.
function isDocumentRoot(el: Element): boolean {
  return el === el.ownerDocument?.body || el.tagName === "HTML";
}

const DOCUMENT_NODE = 9;

/**
 * The element that LOCATES a mutation target. A text node resolves to its
 * parent element; the `document` node resolves to `<html>`. That second case is
 * load-bearing: Next hydrates the whole document (`hydrateRoot(document, <html>)`,
 * verified in next 16.2.11), so a root-level client re-render mutates
 * `document`'s children and arrives with `document` as the record target. An
 * unlocatable node returns null, keeping the caller's `if (path)` guard honest.
 */
function locatingElement(node: Node): Element | null {
  if (node instanceof Element) return node;
  if (node.nodeType === DOCUMENT_NODE) return (node as Document).documentElement ?? null;
  return node.parentElement ?? null;
}

/** A bounded `section#id>div.cls>ul` path naming where in the page a node sits. */
export function describeElementPath(node: Node | null | undefined, depth = PATH_DEPTH): string {
  if (typeof Element === "undefined" || !node) return "";
  const tokens: string[] = [];
  let el: Element | null = locatingElement(node);
  while (el && tokens.length < depth) {
    if (isDocumentRoot(el)) {
      // Only a mutation whose target IS the body degrades to that bare token.
      if (tokens.length === 0) tokens.push(el.tagName.toLowerCase());
      break;
    }
    tokens.unshift(identityToken(el));
    el = el.parentElement;
  }
  return tokens.join(">").slice(0, MAX_PATH_CHARS);
}

const MAX_TEMPLATES = 2;

/**
 * The `<template data-dgst>` markers React streams for a Suspense boundary the
 * server could not finish. Present in the HTML of the failing /free-kundali/
 * run; a client re-render can remove them, so an EMPTY result is a finding
 * (`tpl=none`), not a failure.
 */
export function collectBailoutTemplates(doc: Document): string[] {
  const nodes = Array.from(doc.querySelectorAll("template[data-dgst]")).slice(0, MAX_TEMPLATES);
  return nodes.map(
    (node) =>
      `${(node.getAttribute("data-dgst") ?? "").slice(0, 48)}@${describeElementPath(node.parentElement)}`
  );
}

export interface HydrationEvidenceInput {
  /** React recoverable code, e.g. "418". */
  code: string;
  /** `error.digest` — the server digest React copies off `<template data-dgst>`. */
  digest?: string;
  /** Containers whose children were replaced during hydration (the witness). */
  regenerated: readonly string[];
  /** Bailout templates still in the document at error time. */
  templates: readonly string[];
  /** Earliest foreign node insertions the witness saw (tag + src host only). */
  inserted?: readonly string[];
}

const MAX_REGENERATED = 3;
// Mirror of the beacon sink's per-value cap (beacon-edge.ts sanitizeValue): a
// longer string is truncated in transport, so scrub BEFORE slicing.
const EVIDENCE_MAX_CHARS = 256;

/**
 * The single `component` string carrying the whole identity: which React
 * failure class, which server digest, which container was regenerated, which
 * bailout templates survive. Every `none` is itself a finding.
 */
export function buildHydrationEvidence(input: HydrationEvidenceInput): string {
  const regen = input.regenerated.slice(0, MAX_REGENERATED);
  const parts = [
    `code=${input.code}`,
    `class=${recoverableClass(input.code)}`,
    `dgst=${input.digest ? input.digest.slice(0, 48) : "none"}`,
    `regen=${regen.length ? regen.join(",") : "none"}`,
    `tpl=${input.templates.length ? input.templates.join(",") : "none"}`,
    // The injector, not the symptom: regen=head,body is partially React's own
    // root recovery echo, so per-event attribution (extension vs tag gateway
    // vs app) needs the EARLIEST insertions, captured before recovery swamps
    // the record stream. Host-only for sources — never a path or query.
    `ins=${(input.inserted ?? []).length ? (input.inserted ?? []).slice(0, MAX_REGENERATED).join(",") : "none"}`,
  ];
  // An `id` or class token can embed seeker input (an email in a form-derived
  // id). Scrub the assembled string, then cap — never the other way round.
  return scrubPii(parts.join(";")).slice(0, EVIDENCE_MAX_CHARS);
}
